import React, { useEffect, useRef } from "react";

import * as styles from "./Modal.module.css";

const focusableElements =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

export const Modal = ({ isOpen, onClose, children }: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Prevent background scrolling
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    previousActiveElement.current = document.activeElement as HTMLElement;
    const firstButton = modalRef.current?.querySelector("button");
    firstButton?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      if (e.key === "Tab") {
        const focusableContent = modalRef.current?.querySelectorAll(focusableElements);
        if (!focusableContent || focusableContent.length === 0) {
          return;
        }

        const firstFocusable = focusableContent[0] as HTMLElement;
        const lastFocusable = focusableContent[focusableContent.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      // Restore original overflow when modal closes
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousActiveElement.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleOutsideClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      data-testid="modal-overlay"
      role="presentation"
      onMouseDown={handleOutsideClick}
    >
      <div className={styles.modalWrapper} role="presentation">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          className={styles.modalContent}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
